import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { FiArrowLeft, FiCheck, FiPlus, FiTrash2 } from 'react-icons/fi';

const EmployeeOnboarding = () => {
  const { employeeId } = useParams();
  const { success, error: showError } = useToast();
  const [records, setRecords] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStart, setShowStart] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [tasks, setTasks] = useState(null);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get(`/onboarding/employees/${employeeId}`),
      apiClient.get('/onboarding/templates'),
    ]).then(([recRes, tplRes]) => {
      setRecords(recRes.data.data);
      setTemplates(tplRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load onboarding data')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post(`/onboarding/employees/${employeeId}/start`, { onboardingTemplateId: selectedTemplateId });
      success('✅ Onboarding started');
      setShowStart(false);
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not start onboarding')); }
  };

  const viewTasks = async (recordId) => {
    setSelectedRecordId(recordId);
    setTasks(null);
    try {
      const { data } = await apiClient.get(`/onboarding/employees/${employeeId}/${recordId}/tasks`);
      setTasks(data.data);
    } catch (err) { showError(getErrorMessage(err, 'Could not load tasks')); }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await apiClient.patch(`/onboarding/employees/${employeeId}/${selectedRecordId}/tasks/${taskId}/complete`);
      success('✅ Task marked complete');
      viewTasks(selectedRecordId);
    } catch (err) { showError(getErrorMessage(err, 'Could not complete task')); }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Delete this onboarding record? This removes all its tasks too.')) return;
    try {
      await apiClient.delete(`/onboarding/employees/${employeeId}/${recordId}`);
      success('✅ Onboarding record deleted');
      if (selectedRecordId === recordId) {
        setSelectedRecordId(null);
        setTasks(null);
      }
      loadAll();
    } catch (err) { showError(getErrorMessage(err, 'Could not delete onboarding record')); }
  };

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <Link to="/dashboard/employees" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#6b7280', textDecoration: 'none', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
          <FiArrowLeft size={14} /> Back to Employees
        </Link>
        <h1>Onboarding</h1>
      </div>

      <section className="page-section">
        <div className="section-header">
          <h2>Onboarding Records</h2>
          <button className="btn btn-sm btn-primary" style={{ width: 'auto' }} onClick={() => setShowStart((s) => !s)}>
            <FiPlus size={14} /> {showStart ? 'Cancel' : 'Start Onboarding'}
          </button>
        </div>
        {showStart && (
          <form onSubmit={handleStart} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
            <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
              <label>Template *</label>
              <select required value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
                <option value="">-- select --</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: 'auto' }}>Start</button>
          </form>
        )}
        <div className="table-container">
          <table className="data-table">
            <thead><tr><th>Started</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No onboarding started yet</td></tr>
              ) : records.map((r) => (
                <tr key={r.id}>
                  <td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                  <td><span className="badge" style={{ background: r.status === 'completed' ? '#10b981' : '#f59e0b', color: 'white' }}>{r.status}</span></td>
                  <td>
                    <div className="actions">
                      <button className="btn btn-sm btn-outline" style={{ width: 'auto' }} onClick={() => viewTasks(r.id)}>View Tasks</button>
                      {r.status !== 'completed' && (
                        <button className="action-icon delete" title="Delete" onClick={() => handleDeleteRecord(r.id)}>
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {selectedRecordId && (
        <section className="page-section">
          <div className="section-header"><h2>Tasks</h2></div>
          {tasks === null ? (
            <p style={{ color: '#6b7280' }}>Loading...</p>
          ) : tasks.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No tasks on this onboarding record.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {tasks.map((t) => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{t.onboardingTemplateTask?.taskName || 'Untitled task'}</div>
                    {t.completedAt && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Completed {new Date(t.completedAt).toLocaleDateString()}</div>}
                  </div>
                  {!t.completedAt && (
                    <button className="action-icon edit" title="Mark complete" onClick={() => handleCompleteTask(t.id)}>
                      <FiCheck size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </motion.div>
  );
};

export default EmployeeOnboarding;